import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, momentLocalizer, Event as RBCEvent } from 'react-big-calendar';
import type { ToolbarProps } from 'react-big-calendar';
import moment from 'moment';
import { getEvents, AppEvent } from '../services/eventService';
import CalendarToolbar from '../components/CalendarToolbar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

interface CalendarEvent extends RBCEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: AppEvent;
}

const CalendarPage: React.FC = () => {
  const [events, setEvents] = useState<AppEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<AppEvent | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<'month' | 'week' | 'day'>('month');
  const calendarView = currentView === 'month' ? 'month' : 'agenda';

  const fetchEvents = useCallback(async () => {
    try {
      if (!hasLoaded) {
        setLoading(true);
      }
      setIsFetching(true);
      setError(null);
      const rangeStart = moment(currentDate).startOf('month').subtract(1, 'month').toDate();
      const rangeEnd = moment(currentDate).endOf('month').add(1, 'month').toDate();
      const eventData = await getEvents(rangeStart, rangeEnd);
      setEvents(eventData);
      setHasLoaded(true);
    } catch (error) {
      console.error('Error fetching events:', error);
      const message = error instanceof Error ? error.message : 'Unable to load calendar events right now.';
      setError(message);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [currentDate, hasLoaded]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSelectEvent = (event: CalendarEvent) => {
    setSelectedEvent(event.resource);
  };

  const formatEvents = (): CalendarEvent[] => {
    return events.map(event => ({
      id: event.id,
      title: event.title,
      start: new Date(event.start_time),
      end: new Date(event.end_time),
      resource: event
    }));
  };

  const showError = Boolean(error && !events.length);

  return (
    <div className="page-container">
      <div className="calendar-container">
        <div className="calendar-header">
          <h1 className="page-title">Event Calendar</h1>
          <p className="calendar-subtitle">Browse our calendar and find the perfect date for your event</p>
          {isFetching && <p className="calendar-subtitle">Refreshing calendar…</p>}
          {showError && <p className="calendar-subtitle">{error}</p>}
        </div>

        <div className="calendar-wrapper">
          {loading ? (
            <div className="calendar-loading">
              <div className="calendar-spinner" aria-hidden="true" />
            </div>
          ) : (
            <Calendar<CalendarEvent, object>
              localizer={localizer}
              events={formatEvents()}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 600 }}
              onSelectEvent={handleSelectEvent}
              views={['month', 'agenda']}
              defaultView="month"
              view={calendarView}
              onView={(nextView) => {
                if (nextView === 'month') {
                  setCurrentView('month');
                } else {
                  setCurrentView('day');
                }
              }}
              length={currentView === 'week' ? 7 : 1}
              date={currentDate}
              onNavigate={(newDate) => setCurrentDate(newDate)}
              defaultDate={new Date()}
              components={{
                toolbar: (props: ToolbarProps<CalendarEvent, object>) => (
                  <CalendarToolbar
                    {...props}
                    viewMode={currentView}
                    onViewModeChange={setCurrentView}
                  />
                )
              }}
            />
          )}
        </div>

        {selectedEvent && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>{selectedEvent.title}</h3>
              <p>
                <strong>Description:</strong> {selectedEvent.description || 'No description'}
                {selectedEvent.location ? ` (Space: ${selectedEvent.location})` : ''}
              </p>
              <p><strong>Start:</strong> {moment(selectedEvent.start_time).format('MMMM Do YYYY, h:mm a')}</p>
              <p><strong>End:</strong> {moment(selectedEvent.end_time).format('MMMM Do YYYY, h:mm a')}</p>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedEvent(null);
                  }}
                  className="btn btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarPage;

